import * as React from 'react'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type { CustomerAssessmentDto } from '@acs/shared'
import { apiGet } from '@/lib/api'

/**
 * The questions a customer was asked in their most recent audio assessment.
 *
 * Read only when somebody is about to look at it — the dialog passes `enabled`
 * as its own open state — and prefetched on hover so it is usually already
 * there by the click. The record runs to kilobytes and is looked at
 * deliberately, which is why it is not on the detail (see the note on
 * `getCustomerAssessment` in the API).
 *
 * The key nests under the detail's `['customer', id]` so a prefix invalidation
 * of that customer reaches this too, while `useRealtime`'s exact-key patch of
 * the detail leaves it alone.
 */
export function assessmentKey(id: string): readonly ['customer', string, 'assessment'] {
  return ['customer', id, 'assessment'] as const
}

/**
 * Long enough that a hover-prefetch followed by a click costs one request, not
 * two; short enough that a drawer left open through a re-run shows the new
 * questions on the next open rather than the old ones.
 */
const STALE_MS = 15_000

function assessmentQueryOptions(id: string) {
  return {
    queryKey: assessmentKey(id),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      apiGet<CustomerAssessmentDto | null>(
        `/api/customers/${encodeURIComponent(id)}/assessment`,
        undefined,
        signal
      ),
    staleTime: STALE_MS,
  }
}

export function useCustomerAssessment(
  id: string,
  enabled: boolean
): UseQueryResult<CustomerAssessmentDto | null> {
  return useQuery({ ...assessmentQueryOptions(id), enabled })
}

/**
 * Warm the cache from a hover or a focus. Errors are swallowed by
 * `prefetchQuery` on purpose — a failed prefetch is not an event, the real
 * read on open reports its own failure.
 */
export function usePrefetchAssessment(): (id: string) => void {
  const qc = useQueryClient()
  return React.useCallback(
    (id: string) => {
      void qc.prefetchQuery(assessmentQueryOptions(id))
    },
    [qc]
  )
}
